import { useState } from "react";
import { formFields } from "../../../mocks/contactData";
import ScrollReveal from "../../../components/feature/ScrollReveal";

export default function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Validation
    if (!formData.get("name") || !formData.get("email") || !formData.get("message")) {
      setError("必須項目をすべて入力してください。");
      return;
    }

    const email = formData.get("email") as string;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("正しいメールアドレスを入力してください。");
      return;
    }

    try {
      const response = await fetch("https://readdy.ai/api/form/d7t10oscmq04e0g4es00", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        form.reset();
      } else {
        setError("送信中にエラーが発生しました。時間をおいて再度お試しください。");
      }
    } catch {
      setError("送信中にエラーが発生しました。時間をおいて再度お試しください。");
    }
  };

  if (submitted) {
    return (
      <section className="py-16 md:py-24 bg-airdoa-white">
        <div className="px-4 md:px-8 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-airdoa-cream text-airdoa-gold mb-6">
              <i className="ri-check-line text-2xl" />
            </div>
            <h2 className="text-xl md:text-2xl font-serif font-semibold text-airdoa-navy mb-3">
              お問い合わせを受け付けました
            </h2>
            <p className="text-sm text-airdoa-gray leading-relaxed">
              専任スタッフが内容を確認し、2営業日以内にご返信いたします。
              <br />
              メールが届かない場合は、迷惑メールフォルダもご確認ください。
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 md:py-24 bg-airdoa-white">
      <div className="px-4 md:px-8 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <ScrollReveal direction="up">
            <form
              id="airdoa-contact"
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.name.label}
                  <span className="text-red-400 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder={formFields.name.placeholder}
                  required
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.email.label}
                  <span className="text-red-400 ml-1">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder={formFields.email.placeholder}
                  required
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.phone.label}
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder={formFields.phone.placeholder}
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.inquiryType.label}
                  <span className="text-red-400 ml-1">*</span>
                </label>
                <select
                  name="inquiryType"
                  required
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors appearance-none"
                >
                  <option value="">選択してください</option>
                  {formFields.inquiryType.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.preferredDate.label}
                </label>
                <input
                  type="text"
                  name="preferredDate"
                  placeholder={formFields.preferredDate.placeholder}
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-airdoa-navy mb-1.5">
                  {formFields.message.label}
                  <span className="text-red-400 ml-1">*</span>
                </label>
                <textarea
                  name="message"
                  placeholder={formFields.message.placeholder}
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 text-sm border border-airdoa-gray-light/50 rounded-md bg-white focus:outline-none focus:border-airdoa-gold transition-colors resize-none"
                />
                <p className="mt-1 text-xs text-airdoa-gray">
                  500文字以内でご記入ください
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-100 rounded-md text-sm text-red-500">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary w-full text-center"
              >
                送信する
              </button>
            </form>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}